// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import * as React from 'react'
import Box from '@mui/material/Box'
import Menu from '@mui/material/Menu'
import Grid from "@mui/material/Grid"
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import MenuItem from '@mui/material/MenuItem'
import { languages } from "@/app/i18n/settings"
import { GlassBox } from "@/ui/components/custom/box"
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { TranslationContext } from "@/configs/context"
import { useColorScheme } from '@mui/material/styles'
import { usePathname, useRouter } from "next/navigation"
import {
  alpha,
  Button,
  Divider,
  Stack,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material"
import { appLinks } from "@/lib/links"
import { signOut, useSession } from "next-auth/react"

import MenuIcon from '@mui/icons-material/Menu'
import LoginIcon from '@mui/icons-material/Login'
import SearchIcon from '@mui/icons-material/Search'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import TelegramIcon from '@mui/icons-material/Telegram'
import TranslateIcon from "@mui/icons-material/Translate"
import LightModeIcon from '@mui/icons-material/LightMode'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import WebStoriesIcon from '@mui/icons-material/WebStories'
import AccountCircleIcon from "@mui/icons-material/AccountCircle"
import BrightnessAutoIcon from '@mui/icons-material/BrightnessAuto'

const signInUrl = process.env.NEXT_PUBLIC_ROUTE_SIGNIN ?? "/login"


/**
 * Appbar par défaut de l'application. Ce composant regroupe les différents menus
 * et lien de navigation de à l'application
 * @constructor
 * @name DefaultAppBar
 */
function DefaultAppBar() {
  const theme = useTheme()
  const router = useRouter()
  const { status } = useSession()
  const pathname = usePathname() ?? '/'
  const { mode, setMode, colorScheme } = useColorScheme()

  const isDarkMode = colorScheme === "dark"
  const isConnected = status === 'authenticated'
  const isLoginUI = new RegExp(/\/login/gim).test(pathname)

  const { t, i18n } = React.useContext(TranslationContext)
  const matchSm = useMediaQuery(theme.breakpoints.up('sm'))
  const matchMd = useMediaQuery(theme.breakpoints.up('md'))

  const [addGlassSx, setAddGlassSx] = React.useState<boolean>(false)
  const [showLoginButton, setShowLoginButton] = React.useState<boolean>(false)
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null)
  const [langAnchorEl, setLangAnchorEl] = React.useState<null | HTMLElement>(null)
  const [accountAnchorEl, setAccountAnchorEl] = React.useState<null | HTMLElement>(null)

  const showLangButton = true
  const showGroupedButtons = pathname === '/'

  const pages = [
    {
      label: t('translation:appbar.about'),
      icon: <LocationOnIcon />,
      link: "#footer-section"
    },
    {
      label: t('translation:appbar.contact'),
      icon: <TelegramIcon />,
      link: "#contact-section"
    },
    {
      label: t('translation:appbar.projects'),
      icon: <WebStoriesIcon />,
      link: "#project-section"
    },
    {
      icon: <SearchIcon />,
      link: "#"
    },
  ]

  const handleNavigation = React.useCallback(
    event => {
      const scrollY = event.currentTarget.scrollY

      if (scrollY > 100) {
        setAddGlassSx(true)
      } else {
        setAddGlassSx(false)
      }
    }, []
  )

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget)
  }

  const handleCloseNavMenu = () => {
    setAnchorElNav(null)
  }

  const handleAccountMenuOpen = event => {
    setAccountAnchorEl(event.currentTarget)
  }

  const handleAccountMenuClose = async (param) => {
    setAccountAnchorEl(null)

    if (param === 'disconnect') {
      await signOut({ redirectTo: signInUrl })
    }
  }

  const getThemeIcon = () => {
    if (mode === 'light') return <DarkModeIcon fontSize="small" />
    if (mode === 'dark') return <LightModeIcon fontSize="small" />
    return <BrightnessAutoIcon fontSize="small" />
  }

  const getThemeText = () => {
    if (mode === 'light') return t('translation:appbar.dark_mode')
    if (mode === 'dark') return t('translation:appbar.light_mode')
    return t('translation:appbar.system_mode')
  }

  const handleToggleTheme = () => {
    if (mode === "light") { return setMode("dark") }
    if (mode === "dark") { return setMode("light") }

    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setMode("dark")
    } else {
      setMode("light")
    }
  }

  const handleLangMenuClose = () => {
    setLangAnchorEl(null)
  }

  const handleLangMenuOpen = event => {
    setLangAnchorEl(event.currentTarget)
  }

  // eslint-disable-next-line
  const smoothScroll = (anchor) => {
    if (anchorElNav) { handleCloseNavMenu() }

    document
      .querySelector(anchor)
      .scrollIntoView({}, true)
  }

  React.useEffect(() => {
    window.addEventListener("scroll", handleNavigation)

    return () => {
      window.removeEventListener("scroll", handleNavigation)
    }
  }, [handleNavigation])

  React.useEffect(() => {
    const hideCondition1 = isConnected && showLoginButton
    const hideCondition2 = status === 'unauthenticated' && isLoginUI
    if (hideCondition1 || hideCondition2) {
      return setShowLoginButton(false)
    }

    if ((!showLoginButton || isLoginUI) && status === "unauthenticated") {
      setShowLoginButton(true)
    }
  }, [status, showLoginButton, isLoginUI, isConnected])

  const isLangMenuOpen = Boolean(langAnchorEl)
  const isAccountMenuOpen = Boolean(accountAnchorEl)

  const renderLangMenu = (
    <Menu
      open={isLangMenuOpen}
      anchorEl={langAnchorEl}
      onClose={handleLangMenuClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{
        mt: 5,
        "& .MuiPaper-root": {
          // boxShadow: 'none',
        },
        "& .MuiList-root": {
          // background: "rgba(17, 25, 40, 0.35)",
          // backdropFilter: "blur(10px) saturate(181%)"
        },
      }}
    >
      {[
        {
          lang: t('translation:appbar.english'),
          locale: languages[1],
          isLast: false
        },
        {
          lang: t('translation:appbar.french'),
          locale: languages[0],
          isLast: true
        }
      ].map((langItem) => {
        return (
          <MenuItem
            dense
            key={langItem.lang}
            divider={!langItem.isLast}
            sx={{ py: 0, minHeight: 24 }}
            onClick={() => {
              handleLangMenuClose()
              i18n.changeLanguage(langItem.locale)
            }}
          >
            <Typography variant="caption">{langItem.lang}</Typography>
          </MenuItem>
        )
      })}
    </Menu>
  )

  const renderAcountMenu = (
    <Menu
      open={isAccountMenuOpen}
      anchorEl={accountAnchorEl}
      onClose={handleAccountMenuClose}
      sx={{
        mt: 5,
        "& .MuiPaper-root": {
          // boxShadow: 'none',
        },
        "& .MuiList-root": {
          // background: "rgba(17, 25, 40, 0.35)",
          // backdropFilter: "blur(10px) saturate(181%)"
        },
      }}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      {isConnected &&
        <MenuItem dense divider onClick={() => {
          router.push(
            process.env.NEXT_PUBLIC_ROUTE_ACCOUNT ?? appLinks.account.PROFILE
          )
        }}>
          {t('translation:account.drawer.my_account')}
        </MenuItem>}
      {isConnected &&
        <MenuItem
          dense
          onClick={() => { handleAccountMenuClose('disconnect') }}
        >
          {t('translation:account.appbar.logout')}
        </MenuItem>}
    </Menu>
  )

  let appBarSx: object = {
    background: "transparent !important"
  }

  const iconColor: string = (
    `${alpha(theme.palette.common[isDarkMode ? "white" : "black"], 1)} !important`
  )

  if (addGlassSx) {
    appBarSx = {
      px: 2,
      top: theme.spacing(1),
      borderRadius: "50px !important",
      backdropFilter: "blur(10px) saturate(181%) !important",
      "&.MuiPaper-root": { borderRadius: "50px !important" },
      background: (
        `${alpha(theme.palette.background.paper, isDarkMode ? 0.5 : 0.075)} !important`
      ),
    }
  }

  return (
    <AppBar
      elevation={0}
      position="fixed"
      sx={{ background: "transparent !important" }}
    >
      <Grid
        container
        alignItems="center"
        justifyContent="center"
        spacing={{ xs: 1, lg: 3 }}
        sx={{ px: { xs: 2, lg: 2 } }}
      >
        <Grid size={{ xs: 12, sm: 12, md: 11, lg: 11, xl: 8 }}>
          <Toolbar disableGutters variant="regular" sx={{ ...appBarSx }}>
            <Box
              sx={{
                flexGrow: matchSm ? 1 : 0,
                display: { xs: 'none', md: 'none' }
              }}
            >
              <IconButton
                size="large"
                color="inherit"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                aria-controls="menu-appbar"
                aria-label="account of current user"
              >
                <MenuIcon />
              </IconButton>
              <Menu
                keepMounted
                id="menu-appbar"
                anchorEl={anchorElNav}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{ display: { xs: 'block', md: 'none' } }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                {pages.map((page) => page.label && (
                  <MenuItem
                    href={page.link}
                    key={"m-" + page.label}
                    className="FluidScroll-anchor"
                  >
                    <Typography sx={{ textAlign: 'center' }}>
                      {page.label}
                    </Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Box>

            <Typography
              noWrap
              variant="h6"
              component="a"
              color='textPrimary'
              href="/"
              onClick={(event) => {
                console.log(event)
                event.preventDefault()
                router.push("/")
              }}
              sx={{
                mr: 2,
                fontSize: "1rem",
                fontWeight: 900,
                // color: 'inherit',
                letterSpacing: '.3rem',
                textDecoration: 'none',
                display: { xs: 'flex', md: 'flex' },
              }}
            >
              {t(`translation:appbar.${isLoginUI ? "sarki_ai" : "app_short_name"}`)}

              {/*matchMd &&
                <span style={{ marginLeft: 8 }}>
                  {t('translation:common.by_djotta')}
                </span>*/}
            </Typography>

            <Stack direction="row" justifyContent="center" sx={{ flexGrow: 1 }}>
              {matchMd && showGroupedButtons &&
                <GlassBox
                  sx={{
                    p: 0.25,
                    display: "flex",
                    borderRadius: 50,
                    backdropFilter: "none !important",
                    background: (
                      `${alpha(theme.palette.background.paper, isDarkMode ? 0.1 : 0.05)} !important`
                    )
                  }}
                >
                  {pages.map((page, index) => {
                    const isLastItem = index === pages.length - 1
                    let sx: object = {
                      background: 'default',
                      // border: "1px solid rgba(133, 119, 126, 0.05)",
                    }

                    if (!matchSm) { sx = { ...sx, width: 32, height: 32 } }

                    if (isLastItem) {
                      sx = {
                        ...sx, ml: 4,
                        // background: "rgba(48, 45, 87, 0.8) !important",
                        // "&:hover": {
                        //   background: "rgba(255, 208, 43, 0.24)"
                        // }
                      }
                    }

                    return (
                      <Stack
                        component="a"
                        direction="row"
                        href={page.link}
                        alignItems="center"
                        key={page.label ?? "search"}
                        className={"FluidScroll-anchor"}
                        sx={{
                          my: 0,
                          cursor: "pointer",
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" }
                        }}
                      >
                        <IconButton
                          size="small"
                          variant="glass"
                          sx={{ ...sx, color: iconColor }}
                        >
                          {page.icon}
                        </IconButton>
                        {page.label && matchSm &&
                          <Typography
                            noWrap
                            variant="body2"
                            color='textPrimary'
                            sx={{ ml: 1, fontSize: 12, fontWeight: 500 }}
                          >
                            {page.label}
                          </Typography>}
                        {index < pages.length - 2 &&
                          <Divider
                            flexItem
                            variant="fullWidth"
                            orientation="vertical"
                            sx={{ mx: 3, opacity: matchSm ? 0.5 : 0 }}
                          />}
                      </Stack>
                    )
                  })}
                </GlassBox>}
            </Stack>

            {showLoginButton &&
              <Button
                size="small"
                variant="glass"
                startIcon={<LoginIcon />}
                className={"FluidScroll-anchor"}
                href={process.env.NEXT_PUBLIC_ROUTE_SIGNIN}
                sx={{
                  mx: 1,
                  background: `${alpha(theme.palette.common.black, 1)} !important`
                }}
                onClick={(event) => {
                  event.preventDefault()
                  router.push(process.env.NEXT_PUBLIC_ROUTE_SIGNIN ?? "/login")
                }}
              >
                {t('translation:appbar.login')}
              </Button>}

            {showLangButton &&
              <Tooltip placement="bottom" title={t('translation:appbar.change_language')}>
                <IconButton
                  variant="glass"
                  aria-haspopup="true"
                  sx={{ color: iconColor }}
                  aria-label="language button"
                  onClick={(event) => handleLangMenuOpen(event)}
                >
                  <TranslateIcon fontSize="small" />
                </IconButton>
              </Tooltip>}

            <Tooltip placement="bottom" title={getThemeText()}>
              <IconButton
                variant="glass"
                aria-label="toggle theme"
                onClick={handleToggleTheme}
                sx={{ ml: 1, color: iconColor }}
              >
                {getThemeIcon()}
              </IconButton>
            </Tooltip>
            {isConnected &&
              <Tooltip
                placement="bottom"
                title={t('translation:account.drawer.my_account')}
              >
                <IconButton
                  color="default"
                  variant="glass"
                  aria-haspopup="true"
                  aria-label="account"
                  sx={{ ml: 1, color: iconColor }}
                  onClick={(event) => handleAccountMenuOpen(event)}
                >
                  <AccountCircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>}
          </Toolbar>
        </Grid>
      </Grid>
      {renderLangMenu}
      {renderAcountMenu}
    </AppBar>
  )
}

export default DefaultAppBar
